import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  model,
  OnDestroy,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Project, VersionInfo } from 'src/app/core/objects/project';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { SharedProjectService } from 'src/app/core/services/shared-project.service';
import { CargoSpace, Group, PalletLayerGroup, WorkSpace, OptionsUpdate, ProjectOptions } from 'src/app/core/types';
import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import {
  DisplayedWorkSpace,
  applyPalletWorkspaceQuantityToVehicleLoads,
  palletSpaceQuantity,
  toDisplayedWorkSpaces,
} from 'src/app/shared/helpers/pallet-workspace-collapse.helper';
import {
  cargoSpaceHasLoadedPalletLayers,
  getPalletLayerGroupsFromCargoSpace,
  isAllOptimizationUnavailable,
  isDragDropBetweenCargoSpaceAndOutsideBlocked,
  isDragDropBetweenCargoSpacesBlocked,
  isGroupLockedPalletLayerLoad,
  isGroupRepresentedInPalletLayers,
  listGroupsExcludingPalletLayers,
  projectDisplaysLayeredPallet,
  shouldShowAlgorithmSettingsForCargoSpace,
  workSpaceHasPalletLayers,
} from 'src/app/shared/helpers/pallet-loading-space.helper';
import {
  BACKGROUND_ANIMATION,
  FADE_IN_OUT_ANIMATION,
  HEIGHT_ANIMATION,
} from 'src/app/shared/animations/animations';
import { Message, MessageService } from '../../core/services/message.service';
import { Observable, of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  generateUpdateDragAndDrop,
  getGroupsSorting,
  getProjectGroupsForDropElement,
  syncProjectGroupsAfterCdkDrop,
} from './helpers/loads-list-helpers';

@Component({
    selector: 'gl-loads-list',
    templateUrl: './loads-list.component.html',
    styleUrls: ['./loads-list.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION, BACKGROUND_ANIMATION, HEIGHT_ANIMATION],
    standalone: false
})
export class LoadsListComponent implements OnDestroy {
  @HostBinding('@show') public show;
  @HostBinding('class.overlay') public isOverlay: boolean;
  @ViewChild('list', { static: true }) public list: ElementRef<HTMLElement>;

  public isDragging = model(false);

  public workSpaces: WorkSpace[];
  public displayedWorkSpaces: DisplayedWorkSpace[] = [];
  public currentWorkSpaceIndex: number;
  public currentStopIndex: number;
  public calculateRecommendations: boolean;
  public orderByArrangeOptimally: boolean;
  public orderByLoadsList: boolean;
  public orderByLoadsVolume: boolean;
  public orderByLoadsWeight: boolean;
  public notOverLimit: boolean;
  public simpleAlgorithm: boolean | string;
  public isSimpleAlgorithmDisabled: boolean;
  public isMobile: boolean;
  public dragDisabled = false;
  public isEdditing: boolean;
  public grabbingWorkSpaceIndex: number;
  public animationAfterUpdate: number;
  public isLoadsOptionsActive: boolean;
  public project: Project;
  private closeWorkSpaces: number[];
  public showRecommendations: boolean;
  private subscriptions: Subscription[] = [];
  public currentDisplayIndex: number = null;
  public isSharedProjectLast = true;
  public isSharedView = false;
  public allOptimization = false;
  public isAllOptimizationDisabled = false;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private sharedProjectService: SharedProjectService,
    private renderer: Renderer2,
    private router: Router,
    private messageService: MessageService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    this.isMobile = this.router.url.includes('mobile');
    this.dragDisabled = this.isMobile;

    this.projectService.currentStopIndex.subscribe(
      (currentIndex) => (this.currentStopIndex = currentIndex),
    );
    this.subscriptions.push(
      this.projectService.currentWorkSpaceIndex.subscribe((currentWorkSpaceIndex: number): void => {
        const indexChanged = this.currentWorkSpaceIndex !== currentWorkSpaceIndex;
        this.currentWorkSpaceIndex = currentWorkSpaceIndex;
        if (!indexChanged || currentWorkSpaceIndex < 0) {
          return;
        }
        const workSpace = this.project?.workSpaces?.[currentWorkSpaceIndex];
        if (workSpace && !(workSpace.stops?.length > 0)) {
          this.scrollToCurrentWorkSpace();
        }
      }),
    );
    this.subscriptions.push(
      this.projectService.project.subscribe((project: Project): void => {
        this.cancelRecommendations(project).subscribe();
        this.getProjectVersions();
        this.project = project;
        this.workSpaces = this.project.workSpaces;
        this.displayedWorkSpaces = toDisplayedWorkSpaces(this.workSpaces);
        this.correctNoNameTruck(this.workSpaces);
        this.calculateRecommendations = project.options.calculateRecommendations;
        this.setAlgorithmSettings(project);
        if (this.isProjectEmpty()) {
          this.isLoadsOptionsActive = false;
        }
        this.setRecommendationVisibility(project);
      }),
    );
    this.subscriptions.push(
      this.messageService.getMessages().subscribe((message) => {
        if (message?.name === 'closeRecommendations') {
          this.calculateRecommendations = true;
          this.recommendationsToggleHandler(false);
        }
      }),
    );
    this.projectService.closedLoadingSpaces.subscribe(
      (openedLoadingSpaces: number[]) => (this.closeWorkSpaces = openedLoadingSpaces),
    );

    this.sharedProjectService.isViewingShared.subscribe((isViewingSharedProject) => {
      this.isOverlay = isViewingSharedProject;
      this.isSharedView = isViewingSharedProject;
      this.dragDisabled = isViewingSharedProject;
    });
    this.subscriptions.push(
      this.projectService.closedLoadingSpaces.subscribe(
        (openedLoadingSpaces: number[]) => (this.closeWorkSpaces = openedLoadingSpaces),
      ),
    );

    this.subscriptions.push(
      this.sharedProjectService.isViewingShared.subscribe((isViewingSharedProject) => {
        this.isOverlay = isViewingSharedProject;
        this.isSharedView = isViewingSharedProject;
        this.dragDisabled = isViewingSharedProject;
      }),
    );

    this.getCurrentDisplayIndex();
  }

  private setAlgorithmSettings(project: Project): void {
    if (this.project.workSpaces.length === 0) {
      return;
    }
    const options: ProjectOptions = this.project.workSpaces[this.currentWorkSpaceIndex].options;
    if (!options) {
      return;
    }
    this.orderByLoadsList = options.additionOrder;
    this.orderByArrangeOptimally = options.arrangeOptimally;
    this.simpleAlgorithm = options.newAlgorithm;
    this.orderByLoadsVolume = options.volumeOrder;
    this.orderByLoadsWeight = options.weightOrder;
    this.notOverLimit = !project.options.allowOverweight;
    this.isSimpleAlgorithmDisabled = options.newAlgorithm === 'disabled';
    this.isAllOptimizationDisabled = isAllOptimizationUnavailable(project);
    this.allOptimization =
      project.options.applyArrangeOptimallyOnAllSpaces !== undefined
        ? project.options.applyArrangeOptimallyOnAllSpaces
        : false;
    if (this.isAllOptimizationDisabled && this.allOptimization) {
      this.allOptimization = false;
      this.changeListOrder({ applyArrangeOptimallyOnAllSpaces: false }).subscribe();
    }
    this.simpleAlgorithm = options.newAlgorithm === 'disabled' ? false : !options.newAlgorithm;
    if (!this.showAlgorithmSettings()) {
      this.isLoadsOptionsActive = false;
    }
  }

  public showAlgorithmSettings(): boolean {
    return shouldShowAlgorithmSettingsForCargoSpace(this.getCurrentPrimaryCargoSpace());
  }

  private getCurrentPrimaryCargoSpace(): CargoSpace | undefined {
    const workSpace = this.project?.workSpaces?.[this.currentWorkSpaceIndex];
    if (!workSpace) {
      return undefined;
    }
    if (
      this.currentStopIndex !== undefined &&
      this.currentStopIndex >= 0 &&
      workSpace.stops?.[this.currentStopIndex]
    ) {
      return workSpace.stops[this.currentStopIndex].cargoSpaces?.[0];
    }
    return workSpace.cargoSpaces?.[0];
  }

  public onAllOptimization(): void {
    if (this.isAllOptimizationDisabled) {
      return;
    }
    this.allOptimization = !this.allOptimization;
    this.onOrderChange('orderByArrangeOptimally');
  }

  private setRecommendationVisibility(project: Project): void {
    project.workSpaces.forEach((workSpace: WorkSpace): void => {
      const isAirContainer: boolean = workSpace.cargoSpaces[0].type === 3;
      const isPallet: boolean = workSpace.cargoSpaces[0].type === 4;
      this.showRecommendations = !isAirContainer && !isPallet;
      if (!this.showRecommendations) {
        this.messageService.sendMessage({ name: 'closeWindowRecommendations' });
      }
    });
  }

  private getProjectVersions(): void {
    if (this.isSharedView) {
      this.isSharedProjectLast = true;
      return;
    }
    this.subscriptions.push(
      this.projectApiService.getProjectVersions().subscribe((versions: VersionInfo[]): void => {
        if (versions === null) return;
        const versionsLength: number = versions.length;
        const currentIndex: number = versions.findIndex(
          (version: VersionInfo) => version.isCurrent,
        );
        this.isSharedProjectLast = versionsLength - 1 === currentIndex;
      }),
    );
  }

  private getCurrentDisplayIndex(): void {
    this.subscriptions.push(
      this.messageService.getMessages().subscribe((message: Message): void => {
        if (message.name === 'currentDisplayIndex') {
          this.currentDisplayIndex = message.data as number;
        }
        if (message.name === 'resetMultistops') {
          this.changeStop(this.currentWorkSpaceIndex, 0);
        }
      }),
    );
  }

  private correctNoNameTruck(workSpaces: WorkSpace[]): void {
    workSpaces.forEach((workSpace: WorkSpace): void => {
      if (workSpace.cargoSpaces[0].name.includes('truck_without_name [')) {
        const split: string[] = workSpace.cargoSpaces[0].name.split(' ');
        workSpace.cargoSpaces[0].name = this.translateService.instant(split[0]) + ' ' + split[1];
      }
      workSpace.cargoSpaces[0].groups.forEach((group: Group): void => {
        if (group.name.includes('truck_without_name [')) {
          const split: string[] = group.name.split(' ');
          group.name = this.translateService.instant(split[0]) + ' ' + split[1];
        }
      });
      if (workSpace.cargoSpaces.length > 1) {
        workSpace.cargoSpaces[1].groups.forEach((group: Group): void => {
          if (group.name.includes('truck_without_name [')) {
            const split: string[] = group.name.split(' ');
            group.name = this.translateService.instant(split[0]) + ' ' + split[1];
          }
        });
      }
    });
  }

  public isViewingSharedProject(): Observable<boolean> {
    return this.sharedProjectService.isViewingShared;
  }

  public scrollToCurrentWorkSpace() {
    if (this.list && this.list.nativeElement.children.length > 0) {
      this.list.nativeElement.scrollTop =
        (this.list.nativeElement.children[this.currentWorkSpaceIndex] as HTMLElement)?.offsetTop -
        this.list.nativeElement.offsetTop;
    }
  }

  public isProjectEmpty() {
    return this.projectService.isProjectEmpty();
  }

  public loadsOptionsActive() {
    this.isLoadsOptionsActive = !this.isLoadsOptionsActive;
  }

  public onEditWindow(event: boolean) {
    this.isEdditing = event ? true : false;
    this.sharedProjectService.isViewingShared.subscribe(
      (isViewingShared: boolean) => (this.isOverlay = isViewingShared),
    );
  }

  public recommendationsToggleHandler(isMessage = true): void {
    this.calculateRecommendations = !this.calculateRecommendations;
    const options: OptionsUpdate['options'] = {
      calculateRecommendations: this.calculateRecommendations,
    };
    this.changeListOrder(options).subscribe(() => {
      if (isMessage) {
        if (this.calculateRecommendations) {
          this.messageService.sendMessage({
            name: 'turnOnRecommendations',
          });
        } else {
          this.messageService.sendMessage({
            name: 'turnOffRecommendations',
          });
        }
      }
    });
  }

  public onOrderChange(
    orderBy:
      | 'orderByArrangeOptimally'
      | 'orderByLoadsList'
      | 'orderByLoadsVolume'
      | 'orderByLoadsWeight',
  ) {
    this.orderByArrangeOptimally = orderBy === 'orderByArrangeOptimally' ? true : false;
    this.orderByLoadsList = orderBy === 'orderByLoadsList' ? true : false;
    this.orderByLoadsVolume = orderBy === 'orderByLoadsVolume' ? true : false;
    this.orderByLoadsWeight = orderBy === 'orderByLoadsWeight' ? true : false;
    this.allOptimization =
      !this.orderByArrangeOptimally && this.allOptimization ? false : this.allOptimization;
    const options: OptionsUpdate['options'] = {
      arrangeOptimally: this.orderByArrangeOptimally,
      additionOrder: this.orderByLoadsList,
      volumeOrder: this.orderByLoadsVolume,
      weightOrder: this.orderByLoadsWeight,
      applyArrangeOptimallyOnAllSpaces: this.allOptimization,
    };
    this.changeListOrder(options).subscribe();
  }

  public onNotOverLimitChange(event: boolean) {
    this.changeLoadingOverLimitAllowance(event);
  }

  public checkIsWorkSpaceActive(displayed: DisplayedWorkSpace | number) {
    if (typeof displayed === 'number') {
      return this.currentWorkSpaceIndex === displayed;
    }
    return displayed.index === this.currentWorkSpaceIndex;
  }

  public getPalletLayerGroups(cargoSpace: CargoSpace | undefined): PalletLayerGroup[] {
    return getPalletLayerGroupsFromCargoSpace(cargoSpace).filter(
      (palletGroup) => palletGroup.quantity !== 0,
    );
  }

  public isPalletListCargoSpace(cargoSpace: CargoSpace | undefined): boolean {
    return cargoSpace?.type === LoadingSpaceType.Pallet;
  }

  /** Left list after add: standalone groups and/or persisted pallet layer groups. */
  public showCargoSpaceLoadsList(cargoSpace: CargoSpace | undefined): boolean {
    if (!cargoSpace) {
      return false;
    }
    return (
      this.listGroupsForCargoSpace(cargoSpace).length > 0 ||
      this.getPalletLayerGroups(cargoSpace).length > 0
    );
  }

  /**
   * Empty vehicle cargo spaces stay in the CDK group as drop targets, but take no
   * space until the user starts dragging. Empty pallets without layers stay visible
   * so a load can be dropped onto another pallet.
   */
  public isCargoSpaceDropListHidden(cargoSpace: CargoSpace | undefined): boolean {
    if (this.isPalletListCargoSpace(cargoSpace) && !this.showPalletLayerGroupsInList(cargoSpace)) {
      return false;
    }
    return !this.showCargoSpaceLoadsList(cargoSpace) && !this.isDragging();
  }

  /** Pallet layer breakdown persisted on the cargo space. */
  public showPalletLayerGroupsInList(cargoSpace: CargoSpace | undefined): boolean {
    return this.getPalletLayerGroups(cargoSpace).length > 0;
  }

  /** Hidden for the whole project when the list shows a pallet with layered cargo. */
  public showAddToLoadingSpace(): boolean {
    return !projectDisplaysLayeredPallet(this.project);
  }

  public cargoSpaceGroups(cargoSpace: CargoSpace | undefined): Group[] {
    return listGroupsExcludingPalletLayers(cargoSpace);
  }

  public listGroupsForCargoSpace(cargoSpace: CargoSpace | undefined): Group[] {
    const groups = listGroupsExcludingPalletLayers(cargoSpace);
    if (cargoSpace?.type === LoadingSpaceType.Pallet) {
      return groups;
    }
    return applyPalletWorkspaceQuantityToVehicleLoads(groups, this.workSpaces);
  }

  public isGroupRepresentedInPalletLayers(group: Group, cargoSpace: CargoSpace | undefined): boolean {
    return isGroupRepresentedInPalletLayers(group, cargoSpace);
  }

  /** Disables list DnD for shared/mobile globally or loaded pallet layers. */
  public isDragDisabledForCargoSpace(cargoSpace: CargoSpace | undefined): boolean {
    return this.dragDisabled || cargoSpaceHasLoadedPalletLayers(cargoSpace);
  }

  public isGroupDragDisabled(cargoSpace: CargoSpace | undefined): boolean {
    return this.isEdditing || this.isMobile || cargoSpaceHasLoadedPalletLayers(cargoSpace);
  }

  public isGroupDraggable(cargoSpace: CargoSpace | undefined): boolean {
    return !this.isGroupDragDisabled(cargoSpace);
  }

  public isGroupInteractionLocked(group: Group): boolean {
    return isGroupLockedPalletLayerLoad(group, this.project);
  }

  public isOutsideGroupDragDisabled(group: Group): boolean {
    return (
      this.isEdditing ||
      this.isMobile ||
      this.dragDisabled ||
      this.isGroupInteractionLocked(group)
    );
  }

  public isOutsideGroupDraggable(group: Group): boolean {
    return !this.isOutsideGroupDragDisabled(group);
  }

  public onDragStarted(event: any): void {
    this.isDragging.update(() => true);
    this.renderer.addClass(document.body, 'grabbing');
    const element = event.source.element.nativeElement;
    const listElement = element.closest('.list');
    if (listElement && listElement.dataset.workSpaceIndex) {
      this.grabbingWorkSpaceIndex = +listElement.dataset.workSpaceIndex;
    }
    this.cdr.detectChanges();
  }

  public onDragEnded(): void {
    this.isDragging.update(() => false);
    this.removeGrabbingCursor();
  }

  public isOutsideDropDisabled(workSpaceIndex: number): boolean {
    return workSpaceHasPalletLayers(this.workSpaces?.[workSpaceIndex]);
  }

  public onDropped(event: CdkDragDrop<Group[]>): void {
    // Angular CDK may wrap elements, so we need to find the actual element with data attributes
    // The cdkDropList directive is applied to the element with data attributes
    // We need to traverse up the DOM to find the element with data-work-space-index
    let fromElement = event.previousContainer.element.nativeElement;
    let toElement = event.container.element.nativeElement;

    // Helper function to find element with data attributes
    const findElementWithDataAttributes = (element: HTMLElement): HTMLElement => {
      let current: HTMLElement | null = element;
      while (current) {
        // Check if this element has the data-work-space-index attribute
        if (current.hasAttribute && current.hasAttribute('data-work-space-index')) {
          return current;
        }
        // Move up the DOM tree
        current = current.parentElement;
      }
      return element; // Fallback to original element
    };

    // If the nativeElement doesn't have data attributes, find the parent with data attributes
    if (!fromElement.dataset || !fromElement.dataset.workSpaceIndex) {
      fromElement = findElementWithDataAttributes(fromElement);
    }
    if (!toElement.dataset || !toElement.dataset.workSpaceIndex) {
      toElement = findElementWithDataAttributes(toElement);
    }

    if (this.isDragDropBlockedForDropElements(fromElement, toElement)) {
      return;
    }

    // Create a DragDropEvent-like object from CDK event for compatibility with existing logic
    const dragDropEvent: DragDropEvent = {
      from: fromElement,
      to: toElement
    } as DragDropEvent;

    // If dropping in the same container, reorder
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      // If moving between containers, transfer the item
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Cargo drop lists may bind a filtered copy; sync real project arrays for the API payload.
    const movedGroup = event.container.data[event.currentIndex];
    const projectFromGroups = getProjectGroupsForDropElement(this.project, fromElement);
    const projectToGroups = getProjectGroupsForDropElement(this.project, toElement);
    if (movedGroup && projectFromGroups && projectToGroups) {
      syncProjectGroupsAfterCdkDrop({
        movedGroup,
        cdkFromData: event.previousContainer.data,
        cdkToData: event.container.data,
        projectFromGroups,
        projectToGroups,
        currentIndex: event.currentIndex,
        sameContainer: event.previousContainer === event.container,
      });
    }

    this.updateDragAndDrop(dragDropEvent);
  }

  private isDragDropBlockedForDropElements(fromElement: HTMLElement, toElement: HTMLElement): boolean {
    const fromCargoSpace = this.getCargoSpaceFromDropElement(fromElement);
    const toCargoSpace = this.getCargoSpaceFromDropElement(toElement);
    return (
      isDragDropBetweenCargoSpacesBlocked(fromCargoSpace, toCargoSpace) ||
      isDragDropBetweenCargoSpaceAndOutsideBlocked(
        fromElement,
        toElement,
        fromCargoSpace,
        toCargoSpace,
        this.project,
      )
    );
  }

  private getCargoSpaceFromDropElement(element: HTMLElement): CargoSpace | undefined {
    const dataset = element?.dataset;
    if (!dataset?.workSpaceIndex || dataset.cargoSpaceIndex === undefined) {
      return undefined;
    }
    const workSpaceIndex = Number(dataset.workSpaceIndex);
    const cargoSpaceIndex = Number(dataset.cargoSpaceIndex);
    const stopIndex =
      dataset.stopIndex !== undefined && dataset.stopIndex !== '' ? Number(dataset.stopIndex) : undefined;
    const workSpace = this.project?.workSpaces?.[workSpaceIndex];
    if (!workSpace) {
      return undefined;
    }
    if (stopIndex !== undefined && stopIndex >= 0 && workSpace.stops?.[stopIndex]) {
      return workSpace.stops[stopIndex].cargoSpaces?.[cargoSpaceIndex];
    }
    return workSpace.cargoSpaces?.[cargoSpaceIndex];
  }

  public updateDragAndDrop(event: DragDropEvent) {
    const toElement = event['to'] as HTMLElement;
    // const fromElement = event['from'] as HTMLElement;

    // Ensure we have the dataset - if not, try to get it from the element
    const toElementData: DOMStringMap = toElement?.dataset || {};
    
    // Debug: log if data attributes are missing
    if (!toElementData.workSpaceIndex) {
      console.warn('Missing data-work-space-index on drop target', toElement);
    }

    if (this.isClose(+toElementData.workSpaceIndex)) {
      this.animationAfterUpdate = +toElementData.workSpaceIndex;
    }
    this.removeGrabbingCursor();
    setTimeout(
      () => {
        const fromData = generateUpdateDragAndDrop(
          event,
          'from',
          this.project,
          (workSpaceIndex, cargoSpaceIndex, stopIndex, isUnloadingSpace) =>
            getGroupsSorting(
              workSpaceIndex,
              cargoSpaceIndex,
              stopIndex,
              isUnloadingSpace,
              this.projectService,
            ),
        );
        const toData = generateUpdateDragAndDrop(
          event,
          'to',
          this.project,
          (workSpaceIndex, cargoSpaceIndex, stopIndex, isUnloadingSpace) =>
            getGroupsSorting(
              workSpaceIndex,
              cargoSpaceIndex,
              stopIndex,
              isUnloadingSpace,
              this.projectService,
            ),
        );

        const fromElement = event['from'] as HTMLElement;
        if (
          isDragDropBetweenCargoSpaceAndOutsideBlocked(
            fromElement,
            toElement,
            this.getCargoSpaceFromDropElement(fromElement),
            this.getCargoSpaceFromDropElement(toElement),
            this.project,
          )
        ) {
          return;
        }

        // Check if we're moving between different spaces (cargo space <-> outside space)
        // This is important for backend to properly handle the move
        const isMovingBetweenSpaces =
          fromData.cargoSpaceId !== toData.cargoSpaceId ||
          fromData.stopId !== toData.stopId ||
          fromData.isUnloadingSpace !== toData.isUnloadingSpace;

        this.projectApiService
          .setGroupsOrder({
            currentWorkSpaceId: this.workSpaces[this.currentWorkSpaceIndex].id,
            newOrder: {
              from: fromData,
              to: toData,
            },
          })
          .subscribe(() => {
            // After groups order is updated, the project is updated via handleSuccessWithClearLoads
            // which calls setState and emits new project, triggering visualization update
            // The visualization should automatically re-render via projectService.project subscription
            // However, we ensure visualization is refreshed by sending a message to trigger update
            // Especially important when moving between spaces (cargo <-> outside)
            if (isMovingBetweenSpaces) {
              // Force visualization update when moving between spaces
              this.messageService.sendMessage({ name: 'updateVisualization' });
            }
            this.messageService.sendMessage({ name: 'updateVisualization' });
            this.animationAfterUpdate = undefined;
          });
      },
      300,
    );
  }

  public onAddLoadsToLoadingSpace(workSpaceIndex: number, stopIndex: number): void {
    const workSpaceId: number = this.project.workSpaces[workSpaceIndex].id;
    const stopId: number = stopIndex
      ? this.project.workSpaces[workSpaceIndex].stops[stopIndex].id
      : undefined;
    this.projectApiService.addFromOutside(workSpaceId, stopId, undefined).subscribe();
  }

  public showLoads(workSpaceIndex: number): void {
    if (this.projectService.isClosedLoadingSpace(workSpaceIndex)) {
      this.projectService.openClosedLoadingSpace(workSpaceIndex);
    } else {
      this.projectService.setClosedLoadingSpaces(this.closeWorkSpaces.concat(workSpaceIndex));
    }
  }

  public isClose(workSpaceIndex: number): boolean {
    return this.projectService.isClosedLoadingSpace(workSpaceIndex);
  }

  public isStop(workSpace: WorkSpace): boolean {
    return workSpace.stops?.length > 0;
  }

  public getStopLetterFromIndex(stopIndex: number) {
    return (stopIndex + 10).toString(36);
  }

  public isEmptyCargoSpace(workSpaceIndex: number) {
    if (this.workSpaces[workSpaceIndex].outsideSpace.groups.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  public isEmptyStopCargoSpace(workSpaceIndex: number, stopIndex: number) {
    if (this.workSpaces[workSpaceIndex].stops[stopIndex]?.outsideSpace.groups.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  public changeStop(workspaceIndex: number, stopIndex: number, event: Event = null) {
    this.currentDisplayIndex = null;
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.projectService.setState(this.project, workspaceIndex, stopIndex);
  }

  public trackByFunctionWorkSpace(_index: number, item: WorkSpace): string {
    // return JSON.stringify(item);
    const cargoSpace2: string =
      item.cargoSpaces[1] !== undefined ? `${item.cargoSpaces[1].id}` : '';
    const groupsLength: number =
      item.cargoSpaces[1] !== undefined
        ? item.cargoSpaces[0].groups.length + item.cargoSpaces[1].groups.length
        : item.cargoSpaces[0].groups.length;
    const groupsLength2: number = item.outsideSpace?.groups?.length;
    return `${item.id}${item.cargoSpaces[0].id}${cargoSpace2}${groupsLength}${groupsLength2}`;
  }

  public trackByDisplayedWorkSpace(displayed: DisplayedWorkSpace): string {
    return `${this.trackByFunctionWorkSpace(displayed.index, displayed.workSpace)}:${displayed.quantity}:${displayed.memberIndexes.join(',')}`;
  }

  public onPalletQuantityChange(displayed: DisplayedWorkSpace, quantityDifference: number): void {
    if (!displayed || !this.project || quantityDifference === 0) {
      return;
    }
    const expectedQuantity = Math.max(1, displayed.quantity + quantityDifference);
    this.projectApiService
      .changePalletQuantity({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpaceIndex]?.id,
        target: { workSpaceId: displayed.workSpace.id },
        quantityDifference,
      })
      .subscribe((result: Project) => {
        this.applyPalletQuantity(result, displayed.workSpace.id, expectedQuantity);
      });
  }

  private applyPalletQuantity(
    result: Project,
    workSpaceId: number,
    expectedQuantity: number,
  ): void {
    const project = result ?? this.project;
    const workSpace = project?.workSpaces?.find((space) => space.id === workSpaceId);
    if (!workSpace) {
      return;
    }
    if (palletSpaceQuantity(workSpace) !== expectedQuantity) {
      const cargoSpace = workSpace.cargoSpaces?.[0];
      if (!cargoSpace) {
        return;
      }
      cargoSpace.extraFeatures = {
        ...(cargoSpace.extraFeatures ?? {}),
        palletQuantity: expectedQuantity,
      };
    }
    this.project = project;
    this.workSpaces = project.workSpaces;
    this.displayedWorkSpaces = toDisplayedWorkSpaces(this.workSpaces);
    this.projectService.setState(project);
  }

  public trackByFunction(_index: number, item: Group): string {
    // return JSON.stringify(item);
    const dimensions = `${item.dimensions.width}${item.dimensions.height}${item.dimensions.length}`;
    return `${item.id}${dimensions}${item.quantity}${item.color}${item.name}${_index}${item.createdAt}`;
  }

  private cancelRecommendations(project: Project): Observable<any> {
    const isRecommendationsCalculated: boolean =
      project.recommendations && project.recommendations.length > 0;
    if (isRecommendationsCalculated) {
      return this.projectApiService.closeRecommendation();
    } else {
      this.messageService.sendMessage({ name: 'clearRecommendations' });
      return of();
    }
  }

  // TODO: @acesarski unused code probably
  // private addGrabbingCursor(event: DragDropEvent): void {
  //   this.renderer.addClass(document.body, 'grabbing');
  //   const data = event['from'].dataset;
  //   this.grabbingWorkSpaceIndex = +data.workSpaceIndex;
  // }

  private removeGrabbingCursor(): void {
    this.renderer.removeClass(document.body, 'grabbing');
    this.grabbingWorkSpaceIndex = undefined;
    this.cdr.detectChanges();
  }

  public onDeleteAllTheLoads(workSpaceIndex: number, stopIndex: number): void {
    const workSpaceId: number = this.project.workSpaces[workSpaceIndex].id;
    const stopId: number = stopIndex
      ? this.project.workSpaces[workSpaceIndex].stops[stopIndex]?.id
      : undefined;
    this.projectApiService.deleteOutside(workSpaceId, stopId).subscribe();
  }

  private changeListOrder(options: OptionsUpdate['options']): Observable<any> {
    return this.projectApiService.projectOptionsUpdate({
      currentWorkSpaceId: this.workSpaces[this.currentWorkSpaceIndex]?.id,
      options,
    });
  }

  public simpleAlgorithmChange(event): void {
    this.projectApiService
      .projectOptionsUpdate({
        currentWorkSpaceId: this.workSpaces[this.currentWorkSpaceIndex].id,
        options: {
          newAlgorithm: !event,
        },
      })
      .subscribe();
  }

  private changeLoadingOverLimitAllowance(event: boolean): void {
    this.subscriptions.push(
      this.projectApiService
        .projectOptionsUpdate({
          currentWorkSpaceId: this.workSpaces[this.currentWorkSpaceIndex].id,
          options: {
            allowOverweight: !event,
          },
        })
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}

export interface DragDropEvent extends Event {
  from: HTMLElement;
  to: HTMLElement;
}
